import { createContext, useContext } from "react";
import { Credentials, logIn as userLogIn } from "@/api/auth";
import { Organization } from "@/api/organizations";
import type { AgentViewDescription } from "./agent-provider";

export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within a AppContextProvider");
  }
  return context;
}
export type ConfirmationProps = {
  title?: string;
  description?: string;
  isOpen?: boolean;
  callback?: (checkbox?: boolean) => any;
  confirmText?: string;
  cancelText?: string;
  hideCancel?: boolean;
  checkbox?: boolean;
  checkboxLabel?: string;
};
type AppContextType = {
  initialized?: boolean;
  logIn: (credentials: Credentials) => ReturnType<typeof userLogIn>;
  logOut: () => void;
  actingOrganization?: Organization | null;
  collapseSidebar?: boolean;
  minimizedLeftPanel?: boolean;
  showMobileSidebar?: boolean;
  setShowMobileSidebar: (show: boolean | ((prev: boolean) => boolean)) => void;
  setCollapseSidebar: (collapse: boolean) => void;
  setMinimizedLeftPanel: (minimized: boolean) => void;
  useConfirmation: () => {
    open: (props: ConfirmationProps) => void;
    close: () => void;
    isOpen?: boolean;
    title?: string;
    description?: string;
    callback?: (checkbox?: boolean) => any;
    confirmText?: string;
    cancelText?: string;
    hideCancel?: boolean;
    checkbox?: boolean;
    checkboxLabel?: string;
  };
  swapOrganization: (organization: Organization) => void;
  showAbsoluteSpinner?: boolean;
  setShowAbsoluteSpinner: (show: boolean) => void;
  clipboard?: any;
  setClipboard: (clipboard: any) => void;
  hasPermission: (permission: string) => boolean;
  globalError?: { error: string; callback: () => any } | undefined;
  setGlobalError: (
    value: { error: string; callback: () => any } | undefined,
  ) => void;
  hideSidebar?: boolean;
  setHideSidebar: (hide: boolean) => void;
  impersonatingUser: boolean;
  stopImpersonatingUser: () => void;
  setCurrentChatSessionId: (sessionId?: string) => void;
  currentChatSessionId?: string;
  openAgent: (seedMessage?: string, contextOverride?: AgentViewDescription) => void;
  isAgentMinimized?: boolean;
  setIsAgentMinimized: (minimized: boolean) => void;
  agentSeedMessage?: string;
  clearAgentSeedMessage: () => void;
  agentContextOverride?: AgentViewDescription;
  clearAgentContextOverride: () => void;
  embeddedChatActive?: boolean;
  setEmbeddedChatActive: (active: boolean) => void;
};

export const AppContext = createContext<AppContextType>({
  logIn: async (creds) => await userLogIn(creds), // this is overridden below
  logOut: () => Promise.resolve(undefined),
  setCollapseSidebar: () => Promise.resolve(undefined),
  useConfirmation: () => ({
    open: () => {},
    close: () => {},
    hideCancel: false,
  }),
  swapOrganization: () => {},
  setShowAbsoluteSpinner: () => {},
  setClipboard: () => {},
  hasPermission: () => false,
  setGlobalError: () => {},
  setShowMobileSidebar: () => {},
  setHideSidebar: () => {},
  setMinimizedLeftPanel: () => {},
  stopImpersonatingUser: () => {},
  setCurrentChatSessionId: () => {},
  impersonatingUser: false,
  openAgent: () => {},
  isAgentMinimized: true,
  setIsAgentMinimized: () => {},
  agentSeedMessage: undefined,
  clearAgentSeedMessage: () => {},
  agentContextOverride: undefined,
  clearAgentContextOverride: () => {},
  embeddedChatActive: false,
  setEmbeddedChatActive: () => {},
});
